const ALLOWED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

const WIRE_MIME = {
  'application/x-zip-compressed': 'application/zip',
};

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const TOKEN_TIMEOUT_MS = 30000;

class RequestAQuoteForm {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();
    this.form = root.querySelector('[data-ref="form"]');
    this.fileInput = root.querySelector('[data-ref="file"]');
    this.addLineToggles = Array.from(root.querySelectorAll('[data-ref="add-line"]'));
    this.submitButton = root.querySelector('[data-ref="submit"]');
    this.submitLabel = root.querySelector('[data-ref="submit-label"]');
    this.status = root.querySelector('[data-ref="status"]');
    this.formError = root.querySelector('[data-ref="form-error"]');
    this.turnstileHost = root.querySelector('[data-ref="turnstile"]');
    this.lines = Array.from(root.querySelectorAll('[data-ref="line"]'));
    this.proxyBase = (root.dataset.proxyBase || '').replace(/\/$/, '');

    const catalogTag = root.querySelector('[data-ref="catalog"]');
    const catalog = catalogTag ? JSON.parse(catalogTag.textContent) : {};
    this.messages = catalog.messages ?? {};
    this.rows = new Map((catalog.rows ?? []).map((row) => [row.label, row]));

    this.turnstileToken = null;
    this.turnstileWidget = null;
    this.turnstileWaiters = [];
    this.turnstileUsed = false;
    this.defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent : '';

    this.bind();
    this.mountTurnstile();
  }

  bind() {
    const { signal } = this.controller;

    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });

    // Each line carries its own toggle on the live form and it stays ticked:
    // clearing it hides the line it opened, and anything opened after it.
    for (const toggle of this.addLineToggles) {
      toggle.addEventListener('change', () => this.applyLineToggle(toggle), { signal });
    }

    this.fileInput?.addEventListener(
      'change',
      () => {
        this.showFileName();
        this.validateFile();
      },
      { signal }
    );

    this.lines.forEach((line) => {
      line
        .querySelector('[data-ref="product"]')
        ?.addEventListener('change', () => this.applyRow(line), { signal });
    });

    this.form.querySelectorAll('[required]').forEach((field) => {
      field.addEventListener('blur', () => this.validateField(field), { signal });
    });

    this.updateAddLineButton();
  }

  destroy() {
    this.controller.abort();
  }

  fieldError(field) {
    const describedBy = field?.getAttribute('aria-describedby');
    if (!describedBy) return null;
    return this.root.querySelector(`#${CSS.escape(describedBy.split(' ').pop())}`);
  }

  setFieldError(field, message) {
    if (!field) return;

    const target = this.fieldError(field);
    if (!target) return;

    target.textContent = message;
    field.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  validateField(field) {
    const value = field.value.trim();

    if (!value) {
      this.setFieldError(field, this.messages.required);
      return false;
    }

    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      this.setFieldError(field, this.messages.email);
      return false;
    }

    this.setFieldError(field, '');
    return true;
  }

  // The live page echoes the chosen file in the read-only field beside the button.
  showFileName() {
    const path = this.root.querySelector('[data-ref="file-path"]');
    if (!path) return;
    path.value = this.fileInput?.files?.[0]?.name ?? '';
  }

  validateFile() {
    const file = this.fileInput?.files?.[0];
    if (!file) {
      this.setFieldError(this.fileInput, '');
      return true;
    }

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    const allowedExtensions = ALLOWED_TYPES[file.type];

    if (!allowedExtensions || !allowedExtensions.includes(extension)) {
      this.setFieldError(this.fileInput, this.messages.fileType);
      return false;
    }

    if (file.size > MAX_FILE_BYTES) {
      this.setFieldError(this.fileInput, this.messages.fileSize);
      return false;
    }

    this.setFieldError(this.fileInput, '');
    return true;
  }

  visibleLines() {
    return this.lines.filter((line) => !line.hidden);
  }

  updateAddLineButton() {
    // Kept for the callers that ran before the toggles became per-line; the
    // visibility of each toggle now follows its own line.
  }

  lineByNumber(number) {
    return this.lines.find((line) => line.dataset.line === String(number));
  }

  applyLineToggle(toggle) {
    const target = this.lineByNumber(toggle.dataset.reveals);
    if (!target) return;

    if (toggle.checked) {
      target.hidden = false;
      this.announce(this.messages.lineAdded);
      target.querySelector('select, input')?.focus();
      return;
    }

    // Clearing a toggle closes its line and every line opened beyond it, so the
    // form can never leave an orphan line behind a cleared checkbox.
    for (const line of this.lines) {
      if (Number(line.dataset.line) < Number(toggle.dataset.reveals)) continue;
      line.hidden = true;
      const inner = line.querySelector('[data-ref="add-line"]');
      if (inner) inner.checked = false;
    }
  }

  announce(message) {
    if (!this.status) return;
    this.status.textContent = message;
  }

  mountTurnstile() {
    const siteKey = this.turnstileHost?.dataset.sitekey;
    if (!siteKey) return;

    const render = () => {
      this.turnstileWidget = window.turnstile.render(this.turnstileHost, {
        sitekey: siteKey,
        callback: (token) => this.onTurnstileToken(token),
      });
    };

    if (window.turnstile) {
      render();
      return;
    }

    const interval = setInterval(() => {
      if (!window.turnstile) return;
      clearInterval(interval);
      render();
    }, 200);

    this.controller.signal.addEventListener('abort', () => clearInterval(interval));
  }

  onTurnstileToken(token) {
    const waiter = this.turnstileWaiters.shift();
    if (waiter) {
      this.turnstileUsed = true;
      waiter.resolve(token);
      return;
    }

    this.turnstileToken = token;
  }

  async nextTurnstileToken() {
    if (this.turnstileToken) {
      const token = this.turnstileToken;
      this.turnstileToken = null;
      this.turnstileUsed = true;
      return token;
    }

    const pending = new Promise((resolve, reject) => {
      const waiter = { resolve, reject };
      this.turnstileWaiters.push(waiter);

      setTimeout(() => {
        this.turnstileWaiters = this.turnstileWaiters.filter((entry) => entry !== waiter);
        reject(new Error('turnstile-timeout'));
      }, TOKEN_TIMEOUT_MS);
    });

    if (this.turnstileUsed && this.turnstileWidget !== null) {
      window.turnstile.reset(this.turnstileWidget);
    }

    return pending;
  }

  collectProductLines() {
    return this.visibleLines()
      .map((line) => {
        const productTitle = line.querySelector('[data-ref="product"]').value;
        if (!productTitle) return null;

        const quantity = Number.parseInt(line.querySelector('[data-ref="quantity"]').value, 10);

        return {
          productTitle,
          diameter: line.querySelector('[data-ref="diameter"]').value || undefined,
          format: line.querySelector('[data-ref="format"]').value || undefined,
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : undefined,
        };
      })
      .filter(Boolean);
  }

  applyRow(line) {
    const row = this.rows.get(line.querySelector('[data-ref="product"]').value);
    this.narrow(line.querySelector('[data-ref="diameter"]'), row?.diameters);
    this.narrow(line.querySelector('[data-ref="format"]'), row?.sizes);
  }

  narrow(select, allowed) {
    if (!select) return;

    select.dataset.allOptions ??= JSON.stringify(
      [...select.options].slice(1).map((option) => option.value),
    );

    const every = JSON.parse(select.dataset.allOptions);
    const values = allowed?.length ? every.filter((value) => allowed.includes(value)) : every;
    const previous = select.value;

    select.replaceChildren();
    const empty = document.createElement('option');
    empty.value = '';
    empty.textContent = this.messages.placeholder ?? '';
    select.append(empty);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });

    select.value = values.includes(previous) ? previous : '';
  }

  collectCustomFields() {
    return [...this.form.querySelectorAll('[data-ref="custom-field"]')]
      .map((el) => ({ label: el.dataset.label, value: el.value.trim() }))
      .filter((field) => field.label && field.value);
  }

  async proxyPost(path, body) {
    const response = await fetch(`${this.proxyBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`proxy-${response.status}`);
    }

    return response.json();
  }

  async uploadAttachment(file) {
    const staged = await this.proxyPost('/rfq/upload', {
      turnstileToken: await this.nextTurnstileToken(),
      filename: file.name,
      mimeType: WIRE_MIME[file.type] ?? file.type,
      fileSize: file.size,
    });

    const payload = new FormData();
    staged.parameters.forEach(({ name, value }) => payload.append(name, value));
    payload.append('file', file);

    const upload = await fetch(staged.url, { method: 'POST', body: payload });
    if (!upload.ok) {
      throw new Error(`staged-upload-${upload.status}`);
    }

    return { resourceUrl: staged.resourceUrl, uploadToken: staged.uploadToken };
  }

  setBusy(isBusy) {
    this.submitButton.disabled = isBusy;
    this.submitButton.setAttribute('aria-busy', String(isBusy));
    if (this.submitLabel) {
      this.submitLabel.textContent = isBusy ? this.messages.sending : this.defaultSubmitLabel;
    }
    this.announce(isBusy ? this.messages.sending : '');
  }

  async onSubmit(event) {
    event.preventDefault();

    this.formError.textContent = '';

    const requiredFields = Array.from(this.form.querySelectorAll('[required]'));
    const invalid = requiredFields.filter((field) => !this.validateField(field));
    const fileValid = this.validateFile();

    if (invalid.length || !fileValid) {
      (invalid[0] ?? this.fileInput)?.focus();
      return;
    }

    this.setBusy(true);

    try {
      const file = this.fileInput?.files?.[0] ?? null;
      const upload = file ? await this.uploadAttachment(file) : null;

      const result = await this.proxyPost('/rfq/submit', {
        turnstileToken: await this.nextTurnstileToken(),
        name: this.form.elements.name.value.trim(),
        companyName: this.form.elements.companyName.value.trim(),
        email: this.form.elements.email.value.trim(),
        address: this.form.elements.address.value.trim(),
        city: this.form.elements.city.value.trim(),
        state: this.form.elements.state.value,
        zip: this.form.elements.zip.value.trim(),
        country: this.form.elements.country.value.trim() || undefined,
        phone: this.form.elements.phone.value.trim() || undefined,
        fax: this.form.elements.fax.value.trim() || undefined,
        question: this.form.elements.question.value.trim() || undefined,
        productLines: this.collectProductLines(),
        customFields: this.collectCustomFields(),
        resourceUrl: upload?.resourceUrl,
        uploadToken: upload?.uploadToken,
        attachmentFilename: file?.name,
      });

      window.location.assign(result.redirectUrl);
    } catch (error) {
      console.error('request a quote submission failed', error);
      this.setBusy(false);
      this.formError.textContent = this.messages.generic;
      this.formError.focus();
      this.announce(this.messages.generic);
    }
  }
}

const instances = new WeakMap();

function mount(root) {
  if (instances.has(root)) return;
  instances.set(root, new RequestAQuoteForm(root));
}

document.querySelectorAll('[data-section="request-a-quote"]').forEach(mount);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-section="request-a-quote"]').forEach(mount);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-section="request-a-quote"]').forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  });
});
